<script setup lang="ts">
import { Braces, ChevronDown, ChevronsUpDown, LoaderCircle, Pencil, X } from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"

import AppSelect from "../../components/AppSelect.vue"
import { apiRequest } from "../../lib/api"
import type { AdminCapabilityItem } from "../../lib/types"
import { useSessionStore } from "../../stores/session"

const session = useSessionStore()
const items = ref<AdminCapabilityItem[]>([])
const loading = ref(true)
const error = ref("")
const editing = ref<AdminCapabilityItem | null>(null)
const credits = ref(1)
const capabilityStatus = ref("stable")
const timeoutMs = ref(25000)
const submitting = ref(false)
const expandedProviders = ref<Set<AdminCapabilityItem["provider"]>>(new Set())
const capabilityStatusOptions = [
  { value: "internal", label: "Internal" },
  { value: "beta", label: "Beta" },
  { value: "stable", label: "Stable" },
  { value: "deprecated", label: "Deprecated" },
  { value: "disabled", label: "Disabled" },
]
const platformOrder = [
  "rednote",
  "zhihu",
  "tophub",
  "douban",
  "douyin",
  "weibo",
  "weibo_web",
  "smzdm",
  "bilibili",
  "v2ex",
  "xueqiu",
  "reddit",
  "twitter",
  "xianyu",
  "jd",
  "1688",
  "nowcoder",
  "instagram",
  "maimai",
  "jianyu",
  "toutiao",
  "juejin",
  "36kr",
  "dongchedi",
  "ctrip",
  "linkedin",
] as const
const platformMeta: Record<string, { label: string; name: string; mark: string }> = {
  rednote: { label: "小红书", name: "RedNote", mark: "RED" },
  zhihu: { label: "知乎", name: "Zhihu", mark: "知" },
  tophub: { label: "今日热榜", name: "TopHub", mark: "TOP" },
  douban: { label: "豆瓣", name: "Douban", mark: "豆" },
  douyin: { label: "抖音", name: "Douyin", mark: "抖" },
  weibo: { label: "微博", name: "Weibo", mark: "微" },
  weibo_web: { label: "微博 Web", name: "Weibo Web", mark: "WEB" },
  smzdm: { label: "什么值得买", name: "SMZDM", mark: "值" },
  bilibili: { label: "哔哩哔哩", name: "Bilibili", mark: "哔" },
  v2ex: { label: "V2EX", name: "V2EX", mark: "V2" },
  xueqiu: { label: "雪球", name: "Xueqiu", mark: "雪" },
  reddit: { label: "Reddit", name: "Reddit", mark: "R" },
  twitter: { label: "Twitter / X", name: "Twitter / X", mark: "X" },
  xianyu: { label: "闲鱼", name: "Xianyu", mark: "闲" },
  jd: { label: "京东", name: "JD", mark: "京" },
  "1688": { label: "1688", name: "Alibaba 1688", mark: "1688" },
  nowcoder: { label: "牛客", name: "Nowcoder", mark: "牛" },
  instagram: { label: "Instagram", name: "Instagram", mark: "IG" },
  maimai: { label: "脉脉", name: "Maimai", mark: "脉" },
  jianyu: { label: "剑鱼标讯", name: "Jianyu", mark: "剑" },
  toutiao: { label: "今日头条", name: "Toutiao", mark: "头" },
  sinafinance: { label: "新浪财经", name: "Sina Finance", mark: "财" },
  juejin: { label: "掘金", name: "Juejin", mark: "掘" },
  "36kr": { label: "36氪", name: "36Kr", mark: "36" },
  dongchedi: { label: "懂车帝", name: "Dongchedi", mark: "车" },
  ctrip: { label: "携程", name: "Ctrip", mark: "旅" },
  linkedin: { label: "LinkedIn", name: "LinkedIn", mark: "in" },
}

const groups = computed(() => {
  const providers = [...new Set(items.value.map((item) => item.provider))]
    .sort((left, right) => {
      const leftIndex = platformOrder.indexOf(left as (typeof platformOrder)[number])
      const rightIndex = platformOrder.indexOf(right as (typeof platformOrder)[number])
      return (leftIndex < 0 ? platformOrder.length : leftIndex)
        - (rightIndex < 0 ? platformOrder.length : rightIndex)
        || left.localeCompare(right)
    })
  return providers.map((provider) => {
    const capabilities = items.value
      .filter((item) => item.provider === provider)
      .sort((left, right) => left.operation.localeCompare(right.operation))
    const prices = capabilities.flatMap((item) => (item.credits === null ? [] : [item.credits]))
    const meta = platformMeta[provider] ?? { label: provider, name: provider, mark: provider.slice(0, 3).toUpperCase() }
    return {
      provider,
      ...meta,
      items: capabilities,
      enabledCount: capabilities.filter((item) => ["beta", "stable"].includes(item.status)).length,
      minCredits: prices.length ? Math.min(...prices) : null,
      maxCredits: prices.length ? Math.max(...prices) : null,
    }
  })
})
const allExpanded = computed(
  () => groups.value.length > 0 && groups.value.every((group) => expandedProviders.value.has(group.provider)),
)

onMounted(load)

async function load(): Promise<void> {
  loading.value = true
  try {
    items.value = await apiRequest<AdminCapabilityItem[]>("/v1/admin/capabilities")
    if (expandedProviders.value.size === 0) {
      const firstProvider = groups.value[0]?.provider
      if (firstProvider) expandedProviders.value = new Set([firstProvider])
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载接口额度失败"
  } finally {
    loading.value = false
  }
}

function openEditor(item: AdminCapabilityItem): void {
  expandedProviders.value = new Set([...expandedProviders.value, item.provider])
  editing.value = item
  credits.value = item.credits ?? 1
  capabilityStatus.value = item.status
  timeoutMs.value = item.timeout_ms
}

function toggleProvider(provider: AdminCapabilityItem["provider"]): void {
  const next = new Set(expandedProviders.value)
  if (next.has(provider)) next.delete(provider)
  else next.add(provider)
  expandedProviders.value = next
}

function toggleAll(): void {
  expandedProviders.value = allExpanded.value
    ? new Set()
    : new Set(groups.value.map((group) => group.provider))
}

function priceRange(group: (typeof groups.value)[number]): string {
  if (group.minCredits === null || group.maxCredits === null) return "未定价"
  return group.minCredits === group.maxCredits
    ? `${group.minCredits} credits`
    : `${group.minCredits}–${group.maxCredits} credits`
}

async function save(): Promise<void> {
  if (!editing.value) return
  submitting.value = true
  error.value = ""
  try {
    const updated = await apiRequest<AdminCapabilityItem>(`/v1/admin/capabilities/${editing.value.id}`, {
      method: "PATCH",
      csrfToken: session.csrfToken,
      body: {
        credits_per_unit: credits.value,
        unit: editing.value.unit || "request",
        status: capabilityStatus.value,
        timeout_ms: timeoutMs.value,
      },
    })
    items.value = items.value.map((item) => (item.id === updated.id ? updated : item))
    editing.value = null
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "保存接口额度失败"
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <section class="admin-page">
    <div class="admin-page-heading">
      <div><span class="eyebrow">API economics</span><h1>接口额度</h1><p>管理每次调用的额度消耗、接口开放状态和上游超时。</p></div>
      <div class="admin-count"><strong>{{ items.length }}</strong><span>能力</span></div>
    </div>

    <p v-if="error" class="form-error">{{ error }}</p>
    <div class="capability-directory">
      <div v-if="loading" class="admin-loading"><LoaderCircle class="spin" :size="20" />正在加载接口配置</div>
      <div v-else-if="items.length === 0" class="admin-empty"><Braces :size="22" />暂无已注册接口能力。</div>
      <template v-else>
        <div class="capability-directory__toolbar">
          <span>{{ groups.length }} 个平台 · {{ items.length }} 个接口</span>
          <button type="button" @click="toggleAll"><ChevronsUpDown :size="14" />{{ allExpanded ? "全部收起" : "全部展开" }}</button>
        </div>

        <div class="capability-groups">
          <section v-for="group in groups" :key="group.provider" class="capability-group" :class="{ 'capability-group--open': expandedProviders.has(group.provider) }">
            <button
              type="button"
              class="capability-group__toggle"
              :aria-expanded="expandedProviders.has(group.provider)"
              :aria-controls="`capability-${group.provider}`"
              @click="toggleProvider(group.provider)"
            >
              <span class="capability-group__identity">
                <span class="platform-mark" :class="`platform-mark--${group.provider}`">{{ group.mark }}</span>
                <span><strong>{{ group.name }}</strong><small>{{ group.label }} · {{ group.items.length }} 个接口</small></span>
              </span>
              <span class="capability-group__summary">
                <span><small>开放</small><strong>{{ group.enabledCount }}/{{ group.items.length }}</strong></span>
                <span><small>价格范围</small><strong>{{ priceRange(group) }}</strong></span>
              </span>
              <ChevronDown class="capability-group__chevron" :size="18" />
            </button>

            <div v-show="expandedProviders.has(group.provider)" :id="`capability-${group.provider}`" class="responsive-table capability-group__content">
              <table class="admin-table capability-table">
                <thead><tr><th>接口</th><th>状态</th><th>额度消耗</th><th>计价单位</th><th>超时</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="item in group.items" :key="item.id">
                    <td><strong class="operation-name">{{ item.operation }}</strong><small class="table-subline">{{ item.price_version || "未定价" }}</small></td>
                    <td><span class="status-pill" :class="{ 'status-pill--active': ['beta', 'stable'].includes(item.status) }"><i></i>{{ item.status }}</span></td>
                    <td><strong>{{ item.credits ?? "—" }}</strong> credits</td>
                    <td>{{ item.unit || "—" }}</td>
                    <td>{{ item.timeout_ms.toLocaleString() }} ms</td>
                    <td><button class="icon-button" :aria-label="`编辑 ${item.operation}`" @click="openEditor(item)"><Pencil :size="15" /></button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </template>
    </div>

    <div v-if="editing" class="modal-backdrop" @click.self="editing = null">
      <form class="modal" @submit.prevent="save">
        <div class="modal__heading"><div><span class="eyebrow">Capability pricing</span><h2>{{ editing.operation }}</h2><p>价格采用新版本生效，历史调用账单不会被改写。</p></div><button type="button" class="icon-button" @click="editing = null"><X :size="18" /></button></div>
        <div class="admin-form-grid">
          <label class="field"><span>每次消耗</span><input v-model.number="credits" type="number" min="0" max="1000000" required /></label>
          <label class="field"><span>接口状态</span><AppSelect v-model="capabilityStatus" aria-label="接口状态" :options="capabilityStatusOptions" /></label>
          <label class="field"><span>超时时间（ms）</span><input v-model.number="timeoutMs" type="number" min="100" max="120000" required /></label>
        </div>
        <div class="modal__actions"><button type="button" class="secondary-button" @click="editing = null">取消</button><button class="primary-button" :disabled="submitting"><LoaderCircle v-if="submitting" class="spin" :size="16" />发布新配置</button></div>
      </form>
    </div>
  </section>
</template>

<style scoped>
.capability-directory { border: 1px solid var(--line); background: var(--panel); }
.capability-directory__toolbar { min-height: 48px; display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 0 16px; color: var(--muted); border-bottom: 1px solid var(--line); background: var(--panel-muted); font-size: 10px; }
.capability-directory__toolbar button { display: inline-flex; align-items: center; gap: 6px; padding: 7px 9px; color: var(--muted); background: transparent; cursor: pointer; font-size: 10px; font-weight: 650; }
.capability-directory__toolbar button:hover { color: var(--ink); background: var(--panel); }
.capability-groups { display: grid; }
.capability-group + .capability-group { border-top: 1px solid var(--line); }
.capability-group__toggle { width: 100%; display: grid; grid-template-columns: minmax(250px, 1fr) auto 28px; align-items: center; gap: 28px; min-height: 78px; padding: 12px 17px; color: var(--ink); background: var(--panel); text-align: left; cursor: pointer; }
.capability-group__toggle:hover { background: color-mix(in oklch, var(--panel-muted) 55%, var(--panel)); }
.capability-group--open .capability-group__toggle { background: var(--panel-muted); }
.capability-group__identity { min-width: 0; display: flex; align-items: center; gap: 12px; }
.capability-group__identity .platform-mark { width: 38px; height: 38px; flex: none; }
.capability-group__identity > span:last-child { min-width: 0; display: grid; gap: 3px; }
.capability-group__identity strong { overflow: hidden; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.capability-group__identity small { color: var(--muted); font-size: 9px; }
.platform-mark--tophub { background: oklch(0.48 0.12 18); }
.platform-mark--smzdm { background: oklch(0.51 0.17 24); }
.platform-mark--jd { background: oklch(0.53 0.2 25); }
.platform-mark--xianyu { background: oklch(0.67 0.16 75); }
.platform-mark--1688 { background: oklch(0.59 0.17 50); }
.platform-mark--nowcoder { background: oklch(0.56 0.11 170); }
.platform-mark--instagram { background: linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045); }
.platform-mark--maimai { background: #1677c8; }
.platform-mark--jianyu { background: #9b6115; }
.capability-group__summary { display: grid; grid-template-columns: 78px 126px; gap: 14px; }
.capability-group__summary > span { display: grid; gap: 4px; }
.capability-group__summary small { color: var(--faint); font-size: 8px; text-transform: uppercase; }
.capability-group__summary strong { color: var(--ink-soft); font: 650 10px monospace; white-space: nowrap; }
.capability-group__chevron { color: var(--muted); transition: transform 160ms ease; }
.capability-group--open .capability-group__chevron { transform: rotate(180deg); }
.capability-group__content { border-top: 1px solid var(--line); }
.capability-table { min-width: 850px; }
.capability-table th:first-child, .capability-table td:first-child { padding-left: 68px; }

@media (max-width: 760px) {
  .capability-group__toggle { grid-template-columns: minmax(0, 1fr) 24px; gap: 12px; min-height: 98px; }
  .capability-group__summary { grid-column: 1 / -1; grid-row: 2; grid-template-columns: 78px minmax(120px, 1fr); padding-left: 50px; }
  .capability-group__chevron { grid-column: 2; grid-row: 1; }
  .capability-table th:first-child, .capability-table td:first-child { padding-left: 17px; }
}

@media (max-width: 480px) {
  .capability-directory__toolbar { align-items: start; flex-direction: column; padding-block: 11px; }
  .capability-directory__toolbar button { padding-left: 0; }
}
</style>
